<template>
  <custom-dropdown :label="label" :label-loading="labelLoading" :heading="heading" :disabled="disabled" ref="dropdown"
                   @open="open" :styles="styles">
    <template slot="dropdownToggler">
      <b-tooltip v-if="tooltip" type="is-dark" :label="tooltip" :position="tooltipPlacement" animated>
        <slot></slot>
        <fa v-if="iconSuffix !== null" class="iconSuffix" :icon="iconSuffix"></fa>
      </b-tooltip>
      <slot v-else></slot>
      <fa v-if="iconSuffix !== null" class="iconSuffix" :icon="iconSuffix"></fa>
    </template>
    <template slot="dropdownContent">
      <div v-if="search === true" class="user-search">
        <div class="search-bar">
          <input class="search-bar-input" type="text" v-model="searchTerm" placeholder="Search...">
          <div class="search-icon-container">
            <fa icon="search" class="search-icon"></fa>
          </div>
        </div>
      </div>
      <div class="item-list" :style="styles">
        <template v-for="item in filteredResults">
          <template v-if="item.separator === true">
            <div class="seperator" :title="item.title | title" v-tooltip>
              {{ item.label }}
            </div>
            <div v-for="subItem in item.items" class="item" @click="itemSelected(subItem, item)"
                 :title="subItem.title | title" v-tooltip>
              {{ subItem.label }}
              <fa v-if="subItem.copyValue" class="copy" icon="copy" @click="$copyText(subItem.copyValue)"></fa>
            </div>
          </template>
          <div v-else-if="item.items" class="item" :title="item.title | title" v-tooltip>
            {{ item.label }}
            <div class="item-chevron">
              <fa icon="chevron-right"></fa>
            </div>
            <perfect-scrollbar class="item-list sub-list">
              <div class="item" v-for="subItem in item.items"
                   @click="itemSelected(subItem, item)">
                {{ subItem.label }}
                <fa v-if="subItem.copyValue" class="copy" icon="copy" @click="$copyText(subItem.copyValue)"></fa>
              </div>
            </perfect-scrollbar>
          </div>
          <div v-else-if="item.value !== 'default' && $slots[item.value]" class="item">
            <slot :name="item.value"></slot>
          </div>
          <div v-else class="item" @click="itemSelected(item)" :title="item.title | title" v-tooltip>
            {{ labelAs ? item[labelAs] : item.label }}
            <fa v-if="item.copyValue" class="copy" icon="copy" @click="$copyText(item.copyValue)"></fa>
          </div>
        </template>
      </div>
    </template>
  </custom-dropdown>
</template>

<script>
import filters from 'module/filters';

import CustomDropdown from "component/core/custom-dropdown";

export default {
  components: {
    CustomDropdown
  },
  filters,
  props: {
    value: {
      default: null
    },
    items: {
      type: Array,
      default: () => []
    },
    heading: {
      type: String,
    },
    label: {
      type: String,
      default: null
    },
    labelAs: {
      type: String,
      default: null
    },
    labelLoading: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: String,
      default: null
    },
    tooltipPlacement: {
      type: String,
      default: 'is-bottom'
    },
    iconSuffix: {
      type: String,
      default: null
    },
    search: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showActive: {
      type: Boolean,
      default: false
    },
    returnValueOnly: {
      type: Boolean,
      default: false
    },
    allowDuplicates: {
      type: Boolean,
      default: false
    },
    styles: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      searchTerm: ''
    }
  },
  computed: {
    selected: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    selectedAsObject() {
      if (_.isObject(this.selected) === false) {
        return {
          value: this.selected
        };
      } else {
        return this.selected;
      }
    },
    itemsAsObject() {
      return this.convertItemsToObject(this.items);
    },
    filteredResults() {
      return this.filterItems(this.itemsAsObject);
    }
  },
  methods: {
    open() {
      let searchInput = this.$el.querySelector(".search-bar-input");

      if (this.search === true && searchInput) {
        searchInput.focus();
      }
    },
    convertItemsToObject(items) {
      return items.map((item) => {
        if (_.isObject(item) === false) {
          item = {
            value: item
          }
        }

        item.label = item.label || filters.title(item.value);

        if (item.items) {
          item.items = this.convertItemsToObject(item.items);
        }

        return item;
      });
    },
    filterItems(items, allItems) {

      items = _.cloneDeep(items);

      allItems = allItems || [];

      let checkSearch = this.search === true && this.searchTerm.length >= 1;

      return _.reduce(items, (items, item) => {
        if (item.items) {
          //Don't filter this item but filter the sub items
          item.items = this.filterItems(item.items, allItems);

          if (item.items.length >= 1) {
            items.push(item);
          }
        } else {
          //Filter based on label
          let alreadyExists = this.allowDuplicates === false && allItems.indexOf(item.value) >= 0;
          let inSearch = checkSearch === true && item.label.toLowerCase().indexOf(this.searchTerm.toLowerCase()) >= 0;

          item.active = this.showActive === true && item.value === this.selectedAsObject.value;

          if ((alreadyExists === false && checkSearch === false) || (alreadyExists === false && inSearch === true)) {
            //not already in the list and matches search if search is enabled
            allItems.push(item.value);
            items.push(item);
          }
        }
        return items;
      }, []);
    },
    itemSelected(item, parentItem) {
      event.stopPropagation();

      if (this.returnValueOnly === true) {
        item = item.value;
      }

      if (parentItem && this.returnValueOnly === false) {
        item.parentItem = parentItem;
      }

      this.closeDropdown();

      this.selected = item;
      this.$emit('select', item, parentItem);
    },
    closeDropdown() {
      this.$refs.dropdown.closeDropdown();
    },
  }
}
</script>

<style lang="scss" scoped>

.iconSuffix {
  font-size: .8em;
}

.search-bar {
  position: relative;
  margin-right: 25px;
  width: 100%;
  height: 31px;
  display: flex;
  border-bottom: $color-combo-6-background solid 1px;

  input {
    font-family: inherit;
    height: 30px;
    width: 100%;
    border: 0;
    padding: 0 9px;
    color: $color-combo-6-color;
    font-weight: 700;
    background-color: white;
    z-index: 1;
    flex: 1;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: $color-combo-6-color;
      font-weight: 400;
    }
  }

  .search-icon-container {
    width: 31px;
    height: 100%;
    transition: all .3s ease;
    cursor: pointer;
    background-color: $color-combo-6-background;

    &:hover {
      background-color: $color-combo-6-background;

      .search-icon {
        color: $color-combo-6-color;
      }
    }

    .search-icon {
      position: absolute;
      right: 10px;
      top: 10px;
      color: $color-combo-6-color;
    }
  }
}

.item-list {
  height: 100%;
  max-height: 300px;
  color: $color-combo-6-color;
  overflow-y: auto;

  .item {
    cursor: pointer;
    padding: 2px 5px;
    background-color: $color-combo-6-background;
    transition: all .3s ease;
    text-align: center;
    border-bottom: 1px solid #e8e8e8;
    position: relative;
    font-weight: 400;
    font-size: 11px;

    .item-chevron {
      position: absolute;
      right: 7px;
      top: 50%;
      transform: translateY(-50%);
    }

    &:hover {
      background-color: darken($color-combo-6-background, 10%);

      .sub-list {
        opacity: 1;
        pointer-events: auto;
      }
    }

    .sub-list {
      opacity: 0;
      position: absolute;
      right: 0;
      transform: translateX(100%);
      top: 0;
      border: 5px solid $primary;
      height: fit-content;
      max-height: 150px;
      transition: .2s all ease;
      pointer-events: none;
    }

    .copy {
      opacity: 0.7;
      transition: display 0.5s ease-in-out;
      font-size: .9em;
      cursor: pointer;

      &:hover {
        opacity: 1;
      }
    }
  }

  .seperator {
    background-color: $color-combo-6-background;
    color: $color-combo-6-color;
    padding: 5px;
    font-size: 12px;
    font-weight: 500;
    text-align: center;
  }
}

.assign-user-dropdown {
  .item-list {
    overflow: auto;
  }
}
</style>